import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Package, FileText, Play } from "lucide-react";

export default function ChargementDialog({
  open, onOpenChange, tourneeDetails,
  pendingStatutChange, setPendingStatutChange,
  onDownloadPdf, onConfirmStart,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onOpenChange(false); setPendingStatutChange(null); } else onOpenChange(o); }}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col p-0 gap-0" data-testid="chargement-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            Recapitulatif de chargement
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          <p className="text-sm text-slate-500">Verifiez le chargement avant de demarrer la tournee :</p>
          {(() => {
            const CHAISES_PAR_CARTON = 4;
            const byColor = {};
            if (tourneeDetails) {
              tourneeDetails.livraisons.forEach((l, idx) => {
                const plies = Array.isArray(l.produits_lies) ? l.produits_lies : [];
                if (plies.length > 0) {
                  plies.forEach((pl) => {
                    const key = `${pl.modele || "?"} — ${pl.couleur || "?"}`;
                    const q = Number(pl.quantite) || 0;
                    if (!q) return;
                    if (!byColor[key]) byColor[key] = { modele: pl.modele || "?", couleur: pl.couleur || "", total: 0, clients: [] };
                    byColor[key].total += q;
                    byColor[key].clients.push({ ordre: idx + 1, nom: l.client_nom, qte: q });
                  });
                } else {
                  const key = l.details_commande || "Non specifie";
                  const q = Number(l.quantite) || 1;
                  if (!byColor[key]) byColor[key] = { modele: l.details_commande || "?", couleur: "", total: 0, clients: [] };
                  byColor[key].total += q;
                  byColor[key].clients.push({ ordre: idx + 1, nom: l.client_nom, qte: q });
                }
              });
            }
            const groups = Object.values(byColor).map(g => {
              const cartonsPleins = Math.floor(g.total / CHAISES_PAR_CARTON);
              const reste = g.total % CHAISES_PAR_CARTON;
              const cartonsTotal = cartonsPleins + (reste > 0 ? 1 : 0);
              return { ...g, cartonsPleins, reste, cartonsTotal };
            });
            const totalChaises = groups.reduce((s, g) => s + g.total, 0);
            const totalCartons = groups.reduce((s, g) => s + g.cartonsTotal, 0);

            return (
              <>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-indigo-600 uppercase font-semibold tracking-wider">À charger dans le camion</p>
                    <p className="text-2xl font-bold text-indigo-700 mt-0.5">
                      {totalCartons} carton{totalCartons > 1 ? "s" : ""}
                    </p>
                    <p className="text-xs text-slate-500">{totalChaises} chaise{totalChaises > 1 ? "s" : ""} au total</p>
                  </div>
                  <Package className="w-10 h-10 text-indigo-400" />
                </div>

                <div className="space-y-3">
                  {groups.map((g, idx) => (
                    <div key={idx} className="border rounded-lg overflow-hidden" data-testid={`carton-group-${idx}`}>
                      <div className="bg-slate-50 border-b p-3 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-800">{g.modele}{g.couleur ? ` — ${g.couleur}` : ""}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{g.total} chaise{g.total > 1 ? "s" : ""}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-indigo-700">{g.cartonsTotal} carton{g.cartonsTotal > 1 ? "s" : ""}</p>
                          <p className="text-xs text-slate-500">
                            {g.cartonsPleins > 0 && `${g.cartonsPleins} plein${g.cartonsPleins > 1 ? "s" : ""}`}
                            {g.cartonsPleins > 0 && g.reste > 0 && " + "}
                            {g.reste > 0 && `1 incomplet (${g.reste})`}
                          </p>
                        </div>
                      </div>
                      <div className="p-2 space-y-1">
                        {g.clients.map((c, ci) => (
                          <div key={ci} className="flex items-center justify-between text-xs px-2 py-1 hover:bg-slate-50 rounded">
                            <span className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-[10px]">{c.ordre}</span>
                              <span className="text-slate-700">{c.nom}</span>
                            </span>
                            <span className="font-semibold text-slate-700">{c.qte}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-slate-400 italic px-1">
                  💡 Charge dans l'ordre <b>inverse</b> de livraison : le 1<sup>er</sup> arrêt en dernier (au-dessus / vers la porte).
                </p>
              </>
            );
          })()}
        </div>
        <div className="border-t bg-white px-5 py-3 shrink-0 flex gap-2 flex-wrap" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0.75rem))" }}>
          <Button
            variant="outline"
            className="flex-1 min-w-[120px]"
            onClick={onDownloadPdf}
            data-testid="download-chargement-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Telecharger PDF
          </Button>
          {pendingStatutChange ? (
            <>
              <Button variant="outline" className="flex-1 min-w-[100px]" onClick={() => { onOpenChange(false); setPendingStatutChange(null); }} data-testid="cancel-start-btn">
                Annuler
              </Button>
              <Button className="flex-1 min-w-[140px] bg-green-600 hover:bg-green-700" onClick={onConfirmStart} data-testid="confirm-start-btn">
                <Play className="w-4 h-4 mr-2" />
                Confirmer le depart
              </Button>
            </>
          ) : (
            <Button variant="outline" className="flex-1 min-w-[100px]" onClick={() => onOpenChange(false)} data-testid="close-chargement-btn">
              Fermer
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
